if (!customElements.get('mn-gift-visualizer')) {
  customElements.define('mn-gift-visualizer', class extends HTMLElement {
    connectedCallback() {
      this.initialize = async () => {
        this.root = document.getElementById(this.dataset.root);
        this.input = document.getElementById(this.dataset.input);
        if (!this.root || !this.input) return;
        this.entries = [];
        this.photos = new Map();
        this.variants = JSON.parse(this.querySelector('[data-gift-variants]')?.textContent || '[]');
        try {
          if (this.dataset.config) {
            const response = await fetch(this.dataset.config);
            if (!response.ok) return;
            const catalog = await response.json();
            this.sources = catalog[this.dataset.handle]?.images || [];
          } else {
            this.sources = [{ filename: this.dataset.match, source: this.dataset.source, x: 0.5, y: 0.686, width: 0.46 }];
          }
          await Promise.all(this.sources.map(source => new Promise(resolve => {
            const photo = new Image();
            photo.crossOrigin = 'anonymous';
            photo.onload = () => { this.photos.set(source.filename, photo); resolve(); };
            photo.onerror = resolve;
            photo.src = source.source;
          })));
        } catch { return; }
        if (!this.isConnected) return;
        this.onInput = () => { this.render(); this.showSelectedColor(); };
        this.input.addEventListener('input', this.onInput);
        this.attachCanvases();
        this.render();
        this.observer = new MutationObserver(() => {
          if (this.attachCanvases()) this.render();
          this.installZoom();
        });
        const gallery = this.root.querySelector('.product__media-wrapper');
        if (gallery) this.observer.observe(gallery, { childList: true, subtree: true });
        this.installZoom();
      };
      if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', this.initialize, { once: true });
      else this.initialize();
    }
    sourceFor(image) {
      const url = image.getAttribute('src') || '';
      return this.sources.find(source => url.includes(source.filename) || source.aliases?.some(name => url.includes(name)));
    }
    attachCanvases() {
      let added = false;
      this.root.querySelectorAll('.product__media-wrapper img').forEach(image => {
        const source = this.sourceFor(image), photo = source && this.photos.get(source.filename);
        if (!photo || image.parentElement.querySelector('canvas[data-gift-preview]')) return;
        const canvas = document.createElement('canvas');
        canvas.dataset.giftPreview = '';
        canvas.width = Math.min(photo.naturalWidth, 1600);
        canvas.height = Math.round(canvas.width * photo.naturalHeight / photo.naturalWidth);
        canvas.setAttribute('role', 'img');
        canvas.style.cssText = 'position:absolute;inset:0;width:100%;height:100%;object-fit:contain;pointer-events:none';
        this.entries.push({ image, canvas, source, photo, opacity: image.style.opacity, ariaHidden: image.getAttribute('aria-hidden') });
        image.parentElement.appendChild(canvas);
        image.style.opacity = '0';
        image.setAttribute('aria-hidden', 'true');
        added = true;
      });
      return added;
    }
    render() {
      const caption = (this.input.value.replace(/[\r\n\t]+/g, ' ').trim().slice(0, 24) || this.dataset.defaultCaption).toLocaleUpperCase();
      this.entries?.forEach(({ canvas, source, photo }) => {
        if (!canvas.isConnected) return;
        const context = canvas.getContext('2d');
        if (!context) return;
        context.clearRect(0, 0, canvas.width, canvas.height);
        context.drawImage(photo, 0, 0, canvas.width, canvas.height);
        const maxWidth = canvas.width * (source.width || 0.46);
        let size = canvas.width * 0.04;
        context.font = '700 ' + size + 'px Arial, sans-serif';
        while (context.measureText(caption).width > maxWidth && size > canvas.width * 0.008) {
          size -= 0.5;
          context.font = '700 ' + size + 'px Arial, sans-serif';
        }
        context.fillStyle = '#24221e';
        context.textAlign = 'center';
        context.textBaseline = 'middle';
        if (source.previewOutline) {
          context.strokeStyle = 'rgba(255, 255, 255, 0.75)';
          context.lineWidth = Math.max(1, size * 0.045);
          context.lineJoin = 'round';
          context.strokeText(caption, canvas.width * (source.x || 0.5), canvas.height * (source.y || 0.686));
        }
        context.fillText(caption, canvas.width * (source.x || 0.5), canvas.height * (source.y || 0.686));
        canvas.dataset.renderedText = caption;
        canvas.dataset.color = source.color || '';
        canvas.dataset.textWidth = String(context.measureText(caption).width);
        canvas.dataset.maxWidth = String(maxWidth);
        canvas.setAttribute('aria-label', this.dataset.label + ': ' + caption + (source.color ? ', ' + source.color : ''));
      });
      this.installZoom();
    }
    showSelectedColor() {
      const variantId = this.root.querySelector('product-form input[name="id"]')?.value;
      const variant = this.variants?.find(item => String(item.id) === variantId);
      const gallery = this.root.querySelector('media-gallery');
      if (variant?.featured_media && gallery?.elements?.mediaList && gallery.setActiveMedia) gallery.setActiveMedia(variant);
    }
    installZoom() {
      const gallery = this.root?.querySelector('media-gallery');
      if (!gallery?.lightbox || this.zoomGallery === gallery) return;
      this.zoomGallery = gallery;
      gallery.lightbox.addFilter('itemData', (data, index) => {
        if (!this.isConnected) return data;
        const entry = this.entries.find(item => {
          const slide = item.image.closest('[data-media-index]');
          return slide && Number(slide.dataset.mediaIndex) === index && item.canvas.isConnected;
        });
        if (!entry) return data;
        try {
          return Object.assign({}, data, { src: entry.canvas.toDataURL('image/jpeg', 0.94), width: entry.canvas.width, height: entry.canvas.height });
        } catch { return data; }
      });
    }
    disconnectedCallback() {
      document.removeEventListener('DOMContentLoaded', this.initialize);
      this.input?.removeEventListener('input', this.onInput);
      this.observer?.disconnect();
      this.entries?.forEach(({ canvas, image, opacity, ariaHidden }) => {
        canvas.remove(); image.style.opacity = opacity;
        if (ariaHidden === null) image.removeAttribute('aria-hidden');
        else image.setAttribute('aria-hidden', ariaHidden);
      });
    }
  });
}

